import type {
  GetPolicyLookupCredentialsResponse,
  PolicyLookupCredentialInput,
} from "@platform/automatic-api";
import type { PolicyLookupCompanyInformation } from "@platform/dmv-api";

import { getAccessJwtToken } from "./auth";
import {
  AxleServiceResult,
  automaticBaseUrl,
  callAxleService,
} from "./automatic";

const policyLookupHeaders = async (clientId: string) => ({
  "Content-Type": "application/json",
  Authorization: await getAccessJwtToken(),
  "x-client-id": clientId,
});

export const updatePolicyLookupCompanyInfo = async (
  clientId: string,
  companyInformation: PolicyLookupCompanyInformation
): Promise<
  AxleServiceResult<{ companyInformation: PolicyLookupCompanyInformation }>
> =>
  (await callAxleService(
    `${automaticBaseUrl}/clients/policy-lookup/company-information`,
    {
      method: "post",
      mode: "cors",
      headers: await policyLookupHeaders(clientId),
      body: JSON.stringify({ companyInformation }),
    }
  )) as AxleServiceResult<{
    companyInformation: PolicyLookupCompanyInformation;
  }>;

// Get all policy lookup credentials for a given client and integration
export const getPolicyLookupCredentials = async (
  clientId: string,
  integration?: string
): Promise<AxleServiceResult<GetPolicyLookupCredentialsResponse>> => {
  const query = integration
    ? `?integration=${encodeURIComponent(integration)}`
    : "";

  return (await callAxleService(
    `${automaticBaseUrl}/clients/policy-lookup/credentials${query}`,
    {
      method: "get",
      mode: "cors",
      headers: await policyLookupHeaders(clientId),
    }
  )) as AxleServiceResult<GetPolicyLookupCredentialsResponse>;
};

// Helper function to save a policy lookup credential using POST or PATCH
const savePolicyLookupCredential = async (
  method: "post" | "patch",
  clientId: string,
  integration: string,
  status: "active" | "inactive",
  credential: PolicyLookupCredentialInput
): Promise<AxleServiceResult<Record<string, never>>> =>
  (await callAxleService(
    `${automaticBaseUrl}/clients/policy-lookup/credentials`,
    {
      method,
      mode: "cors",
      headers: await policyLookupHeaders(clientId),
      body: JSON.stringify({ integration, status, credential }),
    }
  )) as AxleServiceResult<Record<string, never>>;

// Create a new policy lookup credential using POST
export const createPolicyLookupCredential = async (
  clientId: string,
  integration: string,
  status: "active" | "inactive",
  credential: PolicyLookupCredentialInput
): Promise<AxleServiceResult<Record<string, never>>> =>
  savePolicyLookupCredential("post", clientId, integration, status, credential);

// Update an existing policy lookup credential using PATCH
export const updatePolicyLookupCredential = async (
  clientId: string,
  integration: string,
  status: "active" | "inactive",
  credential: PolicyLookupCredentialInput
): Promise<AxleServiceResult<Record<string, never>>> =>
  savePolicyLookupCredential(
    "patch",
    clientId,
    integration,
    status,
    credential
  );
